/*
Author: Eli Elad Elrom
Website: https://EliElrom.com
License: MIT License
Component: src/App.tsx
*/

import React from 'react'
import './App.scss'
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box } from '@material-ui/core'
import BubbleChart from './components/BubbleChart/BubbleChart'
import { Types } from './components/BubbleChart/types'

function App() {
  const bubbleChartRef = React.useRef<any>(null)
  
  const [data, setData] = React.useState<Types.Data[]>([])
  const [loading, setLoading] = React.useState<boolean>(true)
  const [generatedAt, setGeneratedAt] = React.useState<string | null>(null)
  const [selectedKeyword, setSelectedKeyword] = React.useState<Types.Data | null>(null)
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false)

  const loadKeywords = React.useCallback(() => {
    fetch('/keywords.json')
      .then((response) => response.json())
      .then((jsonResponse: { generatedAt?: string; keywords?: Types.Data[] }) => {
        // Handle both new format (with generatedAt) and old format (direct array)
        const keywords = jsonResponse.keywords || (Array.isArray(jsonResponse) ? jsonResponse : [])
        setData(keywords)
        if (jsonResponse.generatedAt) {
          setGeneratedAt(jsonResponse.generatedAt)
          console.log('Keywords generated at:', jsonResponse.generatedAt)
        }
        setLoading(false)
      })
      .catch((error) => {
        console.error('Error loading keywords.json:', error)
        setLoading(false)
      })
  }, [])

  // Load keywords on initial mount
  React.useEffect(() => {
    loadKeywords()
  }, [loadKeywords])

  // Set up auto-refresh every 60 seconds
  React.useEffect(() => {
    const intervalId = setInterval(() => {
      console.log('Auto-refreshing keywords data...')
      loadKeywords()
    }, 60 * 1000) // 60 seconds in milliseconds

    // Cleanup interval on unmount
    return () => {
      clearInterval(intervalId)
    }
  }, [loadKeywords])

  const formatDateTime = (isoString: string | null): string => {
    if (!isoString) return ''
    try {
      // Ensure the string is treated as UTC by appending 'Z' if no timezone is present
      // ISO strings without timezone are assumed to be UTC
      let utcString = isoString.trim()
      if (!utcString.endsWith('Z') && !utcString.match(/[+-]\d{2}:\d{2}$/)) {
        utcString = utcString + 'Z'
      }
      
      const date = new Date(utcString)
      // Convert UTC to Brazil timezone (UTC-3, America/Sao_Paulo)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'America/Sao_Paulo'
      })
    } catch {
      return isoString
    }
  }

  const animateBubbles = () => {
    if (bubbleChartRef.current) {
      bubbleChartRef.current.animate()
    }
  }

  const selectedKeyHandler = (key: string) => {
    // Find the keyword data by name
    const keyword = data.find(item => item.name === key)
    
    if (keyword) {
      setSelectedKeyword(keyword)
      setDialogOpen(true)
    }
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setSelectedKeyword(null)
  }

  if (loading) {
    return (
      <div className="App">
        <div className="App-container">
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <p>Loading data...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="App">
      <div className="App-container">
        <div className="App-bubble-container">
          <BubbleChart ref={bubbleChartRef} bubblesData={data} width={1200} height={900} backgroundColor="#ffffff" minValue={1} maxValue={150} selectedCircle={selectedKeyHandler} />
          {generatedAt && (
            <div className="App-timestamp">
              Generated: {formatDateTime(generatedAt)}
            </div>
          )}
        </div>
      </div>
      
      {/* News Dialog Modal */}
      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: '12px',
            padding: '8px'
          }
        }}
      >
        <DialogTitle>
          <Typography variant="h5" component="div" style={{ fontWeight: 'bold' }}>
            {selectedKeyword?.name || 'News'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers style={{ maxHeight: '70vh', overflow: 'auto' }}>
          {selectedKeyword?.news && selectedKeyword.news.length > 0 ? (
            <Box>
              {selectedKeyword.news.map((newsItem, index) => (
                <Box 
                  key={index} 
                  style={{ 
                    marginBottom: index < selectedKeyword.news!.length - 1 ? '10px' : '0',
                    paddingBottom: index < selectedKeyword.news!.length - 1 ? '10px' : '0',
                    borderBottom: index < selectedKeyword.news!.length - 1 ? '1px solid #e0e0e0' : 'none'
                  }}
                >
                  <Typography variant="body2" style={{ lineHeight: '1.4', whiteSpace: 'pre-wrap', fontSize: '0.875rem' }}>
                    {newsItem}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary" style={{ fontSize: '0.875rem' }}>
              No news available for this keyword.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary" variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default App
